import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, IconButton, Switch, TextField,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckIcon from '@mui/icons-material/Check';
import { toast } from 'react-toastify';
import { useAuth } from '../../hook';
import { getRecurringSaleStatus, registerRecurringSale } from './clientApi';
import './client.scss';

const firstDay = (value) => (
  `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-01`
);
const defaultDateForMonth = (value) => {
  const maximum = new Date(value.getFullYear(), value.getMonth() + 1, 0).getDate();
  const day = Math.min(new Date().getDate(), maximum);
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};
const monthName = (value) => new Intl.DateTimeFormat('es-PE', {
  month: 'long', year: 'numeric',
}).format(value);
const readableDate = (value) => {
  if (!value) return 'Sin fecha';
  const [year, month, day] = value.split('-').map(Number);
  return new Intl.DateTimeFormat('es-PE', {
    day: '2-digit', month: 'short', year: 'numeric',
  }).format(new Date(year, month - 1, day));
};
const money = (value, currency) => (
  `${currency === 'USD' ? 'USD' : 'S/'} ${Number(value || 0).toFixed(2)}`
);
const frequencyLabel = (recurring) => ({
  monthly: 'Venta mensual',
  quarterly: 'Venta trimestral',
  yearly: 'Venta anual',
  custom: `Cada ${recurring.interval_months} meses`,
}[recurring.frequency] || 'Venta recurrente');

export function RecurringSales() {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const [month, setMonth] = useState(() => new Date());
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({
    amount: '', issue_date: '', invoiced: false, register_payment: true,
    payment_amount: '', payment_date: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const period = useMemo(() => firstDay(month), [month]);

  const loadRows = async () => {
    setLoading(true);
    setError('');
    try {
      setRows(await getRecurringSaleStatus(auth.token, period));
    } catch (err) {
      setError(err.message || 'No se pudieron cargar las ventas recurrentes.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadRows(); }, [period]); // eslint-disable-line react-hooks/exhaustive-deps

  const moveMonth = (amount) => {
    setSelected(null);
    setMonth((current) => new Date(current.getFullYear(), current.getMonth() + amount, 1));
  };

  const openRegister = (row) => {
    const suggestedDate = defaultDateForMonth(month);
    const validDate = [
      suggestedDate,
      row.recurring.start_date > suggestedDate ? row.recurring.start_date : suggestedDate,
    ].sort().pop();
    const base = Number(row.recurring.default_amount || 0);
    const invoiced = Boolean(row.recurring.invoiced);
    const total = base * (invoiced ? 1.18 : 1);
    const firstPercentage = Number(row.recurring.payment_plan?.[0]?.percentage || 100);
    setSelected(row);
    setForm({
      amount: row.recurring.default_amount || '',
      issue_date: row.recurring.end_date && validDate > row.recurring.end_date
        ? row.recurring.end_date
        : validDate,
      invoiced,
      register_payment: true,
      payment_amount: (total * firstPercentage / 100).toFixed(2),
      payment_date: row.recurring.end_date && validDate > row.recurring.end_date
        ? row.recurring.end_date
        : validDate,
    });
  };

  const recalculatePayment = (amount, invoiced) => {
    const firstPercentage = Number(selected?.recurring.payment_plan?.[0]?.percentage || 100);
    return (Number(amount || 0) * (invoiced ? 1.18 : 1) * firstPercentage / 100).toFixed(2);
  };

  const submit = async () => {
    if (!selected || !form.issue_date || !form.amount || Number(form.amount) <= 0) {
      setError('Ingresa una fecha y un monto mayor que cero.');
      return;
    }
    if (
      form.register_payment
      && (!form.payment_date || !form.payment_amount || Number(form.payment_amount) <= 0)
    ) {
      setError('Completa la fecha y el monto del pago.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await registerRecurringSale(auth.token, selected.recurring.id, {
        ...form, period,
      });
      setSelected(null);
      toast.success('Venta recurrente registrada correctamente.');
      await loadRows();
    } catch (err) {
      setError(err.message || 'No se pudo registrar la venta.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="client-page client-expense-page client-recurring-sales-page">
      <header className="client-header">
        <IconButton onClick={() => navigate('/')}><ArrowBackIosNewIcon fontSize="small" /></IconButton>
        <h1>Ventas recurrentes</h1><span />
      </header>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      <section className="client-recurring-sales-intro">
        <strong>Ventas previstas</strong>
        <small>Registra las ventas correspondientes al periodo seleccionado.</small>
      </section>
      <div className="client-month-picker">
        <IconButton onClick={() => moveMonth(-1)} aria-label="Mes anterior"><ChevronLeftIcon /></IconButton>
        <strong>{monthName(month)}</strong>
        <IconButton onClick={() => moveMonth(1)} aria-label="Mes siguiente"><ChevronRightIcon /></IconButton>
      </div>
      <p className="client-month-help">Por defecto se muestra el mes actual</p>

      {loading ? <div className="client-empty"><CircularProgress size={28} /></div> : (
        <div className="client-recurring-list">
          {!rows.length && <div className="client-empty">No hay ventas recurrentes para este mes.</div>}
          {rows.map((row) => {
            const registered = row.occurrence.status === 'registered';
            return (
              <article className={`client-recurring-card client-recurring-sale-card${registered ? ' client-recurring-card--paid' : ''}`} key={row.recurring.id}>
                <div className="client-recurring-card__content">
                  <span className="client-recurring-card__eyebrow">
                    {frequencyLabel(row.recurring)}
                  </span>
                  <div className="client-recurring-card__name">{row.recurring.client?.name || 'Sin cliente'}</div>
                  <div className="client-recurring-card__meta">
                    {row.recurring.name}
                    {row.recurring.primary_service?.name ? ` · ${row.recurring.primary_service.name}` : ''}
                  </div>
                  <div className="client-recurring-card__validity">
                    Vigente desde {readableDate(row.recurring.start_date)}
                    {row.recurring.end_date ? ` hasta ${readableDate(row.recurring.end_date)}` : ' · sin fecha de fin'}
                  </div>
                  {registered && <div className="client-recurring-card__meta">
                    Registrada el {readableDate(row.occurrence.registered_at)}
                  </div>}
                </div>
                <div className="client-recurring-card__aside">
                  <small>Subtotal</small>
                  <strong className="client-recurring-card__amount">
                    {money(
                      registered ? row.occurrence.amount_registered : row.recurring.default_amount,
                      row.recurring.currency,
                    )}
                  </strong>
                  {registered ? (
                    <span className="client-recurring-card__status"><CheckIcon fontSize="small" /> Registrada</span>
                  ) : (
                    <Button size="small" variant="contained" onClick={() => openRegister(row)}>Registrar</Button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}

      <Dialog className="client-expense-dialog" open={Boolean(selected)}
        onClose={saving ? undefined : () => setSelected(null)} fullWidth maxWidth="xs">
        <DialogTitle>Registrar venta recurrente</DialogTitle>
        <DialogContent className="client-expense-dialog__form">
          <div className="client-recurring-dialog-summary">
            <strong>{selected?.recurring.client?.name}</strong>
            <span>{selected?.recurring.name} · {monthName(month)}</span>
            <small>El monto corresponde al subtotal antes de IGV.</small>
          </div>
          <TextField label={`Monto (${selected?.recurring.currency || 'PEN'})`} type="number"
            value={form.amount} inputProps={{ min: '0.01', step: '0.01' }}
            onChange={(event) => {
              const amount = event.target.value;
              setForm({
                ...form, amount,
                payment_amount: recalculatePayment(amount, form.invoiced),
              });
            }} />
          <TextField label="Fecha de la venta" type="date" value={form.issue_date}
            InputLabelProps={{ shrink: true }}
            onChange={(event) => setForm({
              ...form, issue_date: event.target.value, payment_date: event.target.value,
            })} />
          <div className="client-recurring-tax-control">
            <FormControlLabel label="Venta facturada (añadir IGV 18%)"
              control={<Switch checked={form.invoiced}
                onChange={(event) => {
                  const invoiced = event.target.checked;
                  setForm({
                    ...form, invoiced,
                    payment_amount: recalculatePayment(form.amount, invoiced),
                  });
                }} />} />
          </div>
          <div className="client-recurring-register-totals">
            <div><span>Subtotal</span><strong>{money(form.amount, selected?.recurring.currency)}</strong></div>
            <div><span>IGV</span><strong>{money(
              form.invoiced ? Number(form.amount || 0) * 0.18 : 0,
              selected?.recurring.currency,
            )}</strong></div>
            <div><span>Total</span><strong>{money(
              Number(form.amount || 0) * (form.invoiced ? 1.18 : 1),
              selected?.recurring.currency,
            )}</strong></div>
          </div>
          <div className="client-recurring-payment-fields">
            <FormControlLabel label="Registrar pago ahora"
              control={<Switch checked={form.register_payment}
                onChange={(event) => setForm({
                  ...form, register_payment: event.target.checked,
                })} />
              } />
            {form.register_payment && <>
              <TextField label={`Monto del pago (${selected?.recurring.currency || 'PEN'})`}
                type="number" value={form.payment_amount}
                inputProps={{ min: '0.01', step: '0.01' }}
                helperText={`Cuota 1: ${selected?.recurring.payment_plan?.[0]?.percentage || 100}% del total`}
                onChange={(event) => setForm({ ...form, payment_amount: event.target.value })} />
              <TextField label="Fecha del pago" type="date" value={form.payment_date}
                InputLabelProps={{ shrink: true }}
                onChange={(event) => setForm({ ...form, payment_date: event.target.value })} />
              {form.invoiced && selected?.recurring.detraction_applied && (
                <div className="client-recurring-detraction-note">
                  <span>Detracción incluida</span>
                  <strong>{money(
                    Number(form.payment_amount || 0)
                      * Number(selected.recurring.detraction_rate || 0) / 100,
                    selected.recurring.currency,
                  )}</strong>
                </div>
              )}
            </>}
          </div>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button color="inherit" disabled={saving} onClick={() => setSelected(null)}>Cancelar</Button>
          <Button variant="contained" disabled={saving} onClick={submit}>
            {saving ? 'Guardando...' : 'Registrar venta'}
          </Button>
        </DialogActions>
      </Dialog>
    </main>
  );
}
